import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";

import {
  CampaignType,
  MatchType,
  SpStrategyCreateRequest,
  StrategyStateEnum,
  StrategyType,
  Targeting,
} from "@front/m19-api-client";
import { getStrategies, MaxPriority, StrategyTargetingType } from "@front/m19-models";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { validateAlgoMode } from "@m19-board/shared/algo-mode-selection/algo-mode-selection.component";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { IButtonDirective, IStepperComponent, MODAL_DATA, ModalRef, ToasterService } from "@front/m19-ui";
import {
  SpCreateStrategySetupComponent,
  SpCreateStrategySetupModel,
} from "@m19-board/sponsored-product/sp-creation/steps/sp-create-strategy-setup.component";
import {
  SpCreateStrategyAlgorithmComponent,
  SpCreateStrategyAlgorithmModel,
} from "@m19-board/sponsored-product/sp-creation/steps/sp-create-strategy-algorithm.component";
import {
  SpCreateStrategyAsinsComponent,
  SpCreateStrategyAsinsModel,
} from "@m19-board/sponsored-product/sp-creation/steps/sp-create-strategy-asins.component";
import {
  SpCreateStrategyTargetingsComponent,
  SpCreateStrategyTargetingsModel,
} from "@m19-board/sponsored-product/sp-creation/steps/sp-create-strategy-targetings.component";
import { applyWhen, form, maxLength, minLength, required, validate } from "@angular/forms/signals";
import { FormNavigationService, IFormStep } from "@m19-board/shared/form-navigation/form-navigation.service";
import { FormShellComponent } from "@m19-board/shared/form-shell/form-shell.component";
import { AccountSelectionService, SpStrategyService } from "@front/m19-services";
import { finalize, map, switchMap, take } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { NavBreadcrumbDirective } from "@m19-board/containers/page-layout/nav-breadcrumb.directive";
import { TitleCasePipe } from "@angular/common";

interface SpSubStrategyCreationFormModel {
  setup: SpCreateStrategySetupModel;
  algorithm: SpCreateStrategyAlgorithmModel;
  asins: SpCreateStrategyAsinsModel;
  targetings: SpCreateStrategyTargetingsModel;
}

@Component({
  selector: "app-sp-strategy-creation",
  templateUrl: "./sp-strategy-creation.component.html",
  providers: [FormNavigationService],
  imports: [
    TranslocoDirective,
    ReactiveFormsModule,
    IButtonDirective,
    IStepperComponent,
    SpCreateStrategyAlgorithmComponent,
    SpCreateStrategyAsinsComponent,
    SpCreateStrategySetupComponent,
    FormShellComponent,
    SpCreateStrategyTargetingsComponent,
    NavBreadcrumbDirective,
    RouterLink,
    TitleCasePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpStrategyCreationComponent implements OnInit {
  private readonly translocoService = inject(TranslocoService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly strategyService = inject(SpStrategyService);
  private readonly accountSelection = inject(AccountSelectionService);
  private readonly toastrService = inject(ToasterService);
  private readonly formNavigationService = inject(FormNavigationService<SpSubStrategyCreationFormModel>);
  protected readonly modalData = inject<SpStrategyCreationModalData>(MODAL_DATA, { optional: true });
  private readonly modalRef = inject(ModalRef, { optional: true });

  readonly StrategyType = StrategyType;
  readonly MatchType = MatchType;
  readonly StrategyTypeStrKeys: { [key in StrategyType]: string } = {
    [StrategyType.LEGACY]: "",
    [StrategyType.PRODUCT]: this.translocoService.translate("sp-substrategy-creation.main"),
    [StrategyType.BRAND]: this.translocoService.translate("sp-substrategy-creation.brand_defense"),
    [StrategyType.KEYWORD]: this.translocoService.translate("sp-substrategy-creation.focus"),
  };

  isSubStrategyCreation = input<boolean>(true);

  strategyGroup = toSignal(
    this.activatedRoute.paramMap.pipe(
      switchMap((params) => {
        const id = Number(params.get("id"));
        return this.accountSelection.singleAccountMarketplaceSelection$.pipe(
          switchMap((am) => this.strategyService.getStrategyGroups(am.accountId, am.marketplace)),
          map((strategyGroups) => strategyGroups.get(id)),
        );
      }),
    ),
  );

  private readonly strategyTypeParam = toSignal(
    this.activatedRoute.queryParamMap.pipe(map((params) => params.get("strategyType") as StrategyType)),
    { requireSync: true },
  );

  strategyType = computed(() => (this.isSubStrategyCreation() ? this.strategyTypeParam() : StrategyType.PRODUCT));
  strategyTypeStr = computed(() => this.StrategyTypeStrKeys[this.strategyType()!]);

  readonly accountMarketplace = toSignal(this.accountSelection.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });

  readonly strategies = toSignal(
    this.accountSelection.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.strategyService.getStrategies(am.accountId, am.marketplace)),
      map((strategies) => Array.from(strategies.values())),
    ),
  );

  formModel = signal<SpSubStrategyCreationFormModel>({
    setup: {
      strategyName: "",
      onlyB2B: false,
    },
    algorithm: {
      algoModeConfig: null,
    },
    asins: {
      asins: this.modalData?.asins ?? [],
    },
    targetings: {
      targetingType: null,
      keywords: [],
      productTargetings: [],
    },
  });

  creationForm = form(
    this.formModel,
    (schema) => {
      required(schema.setup.strategyName);
      maxLength(schema.setup.strategyName, 80);

      required(schema.asins.asins);
      minLength(schema.asins.asins, 1);

      required(schema.algorithm.algoModeConfig);
      validateAlgoMode(schema.algorithm.algoModeConfig);

      applyWhen(
        schema,
        () => this.strategyType() === StrategyType.BRAND,
        (schema) =>
          validate(schema.targetings, ({ value }) =>
            value().keywords.length + value().productTargetings.length > 0 ? null : { kind: "missingTargeting" },
          ),
      );

      applyWhen(
        schema,
        () => this.strategyType() === StrategyType.KEYWORD,
        (schema) =>
          validate(schema.targetings, ({ value }) => {
            const { targetingType, keywords, productTargetings } = value();
            const selectedTargetings =
              targetingType === StrategyTargetingType.KEYWORDS
                ? keywords
                : targetingType === StrategyTargetingType.PRODUCTS
                  ? productTargetings
                  : [];
            return selectedTargetings.length > 0 ? null : { kind: "missingTargeting" };
          }),
      );
    },
    {
      submission: {
        action: async () => this.submitForm(),
      },
    },
  );

  readonly currentStepId = this.formNavigationService.currentStepId;
  readonly formSteps = this.formNavigationService.formSteps;
  readonly isFirstStep = this.formNavigationService.isFirstStep;
  readonly isLastStep = this.formNavigationService.isLastStep;
  readonly canGoNext = this.formNavigationService.canGoNext;

  isCreatingStrategy = signal(false);

  allowTargetings = computed(() => this.isSubStrategyCreation() && this.strategyType() !== StrategyType.PRODUCT);

  ngOnInit(): void {
    this.formNavigationService.configure({
      form: this.creationForm,
      blocked: this.isCreatingStrategy,
      steps: [
        {
          stepId: "setup",
          label: this.translocoService.translate("common.setup"),
          size: "sm",
        },
        {
          stepId: "algorithm",
          label: this.translocoService.translate("common.algorithm"),
          size: "sm",
        },
        {
          stepId: "asins",
          label: this.translocoService.translate("common.asins"),
          size: "md",
        },
        {
          stepId: "targetings",
          label: this.translocoService.translate("common.targetings"),
          size: "md",
          showWhen: this.allowTargetings,
        },
      ],
    });
  }

  goToStep(stepId: IFormStep<SpSubStrategyCreationFormModel>) {
    this.formNavigationService.goToStep(stepId);
  }

  cancel() {
    if (this.modalData) {
      this.modalRef?.close();
    } else {
      this.router.navigate([".."], { relativeTo: this.activatedRoute });
    }
  }

  back() {
    this.formNavigationService.back();
  }

  next() {
    this.formNavigationService.next();
  }

  private computePriority() {
    if (!this.strategyGroup()) return undefined;

    if (this.strategyType() != StrategyType.BRAND && this.strategyType() != StrategyType.KEYWORD) {
      return undefined;
    }
    const strategies = getStrategies(this.strategyGroup()!, this.strategyType());
    if (strategies.length == 0) {
      return undefined;
    }
    return Math.floor((strategies[strategies.length - 1].priority! + MaxPriority) / 2);
  }

  submitForm() {
    if (this.creationForm().invalid()) {
      return;
    }
    this.isCreatingStrategy.set(true);

    const formValue = this.creationForm().value();

    const { targetingType, keywords, productTargetings } = formValue.targetings;
    const isBrand = this.strategyType() === StrategyType.BRAND;
    const selectedKeywords = isBrand || targetingType === StrategyTargetingType.KEYWORDS ? keywords : [];
    const selectedProducts = isBrand || targetingType === StrategyTargetingType.PRODUCTS ? productTargetings : [];
    const targetings: Targeting[] = [
      ...selectedKeywords,
      ...selectedProducts.map((asin) => ({ targetingValue: asin, matchType: MatchType.asinSameAs })),
    ];

    const isStrategyLimitReached = this.strategyService.isStrategyLimitReached(
      this.accountMarketplace().accountId,
      this.strategies() ?? [],
      CampaignType.SP,
    );

    const createRequest: SpStrategyCreateRequest = {
      state: isStrategyLimitReached ? StrategyStateEnum.PAUSED : StrategyStateEnum.ENABLED,
      strategyGroupId: this.strategyGroup()?.strategyGroupId,
      targetings,
      name: formValue.setup.strategyName,
      ...formValue.algorithm.algoModeConfig!,
      acosTarget: formValue.algorithm.algoModeConfig!.acosTarget
        ? formValue.algorithm.algoModeConfig!.acosTarget / 100
        : undefined,
      asins: formValue.asins.asins ?? [],
      priority: this.computePriority(),
      onlyB2B: formValue.setup.onlyB2B,
    };

    if (this.isSubStrategyCreation()) {
      this.createSubStrategy(createRequest);
    } else {
      this.createStrategyGroup(createRequest);
    }
  }

  private createStrategyGroup(createRequest: SpStrategyCreateRequest): void {
    this.strategyService
      .createStrategyGroup(
        {
          accountId: this.accountMarketplace().accountId,
          marketplace: this.accountMarketplace().marketplace,
          strategyGroupName: createRequest.name!,
        },
        createRequest,
      )
      .pipe(
        take(1),
        finalize(() => this.isCreatingStrategy.set(false)),
      )
      .subscribe({
        next: (strategyGroup) => {
          this.toastrService.success(
            this.translocoService.translate("sp-strategy-creation.strategy_group_created_successfully"),
            this.translocoService.translate("sp-strategy-creation.strategy_group_created"),
          );

          if (!this.modalData) {
            this.router.navigate(["/advertising/sponsored-product/strategy-group/" + strategyGroup.strategyGroupId], {
              queryParamsHandling: "merge",
            });
          }

          this.modalRef?.close(true);
        },
        error: (e: string) => {
          this.toastrService.error(
            e,
            this.translocoService.translate("sp-strategy-creation.strategy_group_creation_error"),
          );
        },
      });
  }

  private createSubStrategy(createRequest: SpStrategyCreateRequest): void {
    createRequest.strategyType = this.strategyType();

    this.strategyService
      .createStrategy(createRequest)
      .pipe(
        take(1),
        finalize(() => {
          this.isCreatingStrategy.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("sp-substrategy-creation-page.type_strategy_created", [
              this.strategyTypeStr(),
            ]),
          );
          this.router.navigate(
            ["/advertising/sponsored-product/strategy-group/" + this.strategyGroup()!.strategyGroupId],
            {
              queryParamsHandling: "merge",
            },
          );
        },
        error: (e: string) => {
          this.toastrService.error(
            e,
            this.translocoService.translate("sp-substrategy-creation-page.type_strategy_creation_error", [
              this.strategyTypeStr(),
            ]),
          );
        },
      });
  }
}

export interface SpStrategyCreationModalData {
  asins: string[];
}
